import { useCallback, useEffect, useRef } from 'react';
import { type EditableSiteMenuOrder, toCartCustomerOrder } from './customer-order-form.utils';
import { useCartContext } from '../../../../contexts/cart.context';
import { LocalDate } from '../../../../utils/local-date';

interface NewOrderDraft {
  customerId: string | undefined;
  orderDate: LocalDate | undefined;
  siteMenuOrders: EditableSiteMenuOrder[];
}

/**
 * Keeps the new order being placed in the cart when the page is left or reloaded, so it can be
 * restored later. The cart is cleared when the order has no site menu orders, or once it is submitted.
 *
 * Returns the function to call once the order is submitted.
 */
const useKeepNewOrderInCart = (isNewOrder: boolean, draft: NewOrderDraft): (() => void) => {
  const { setCart, clearCart } = useCartContext();
  const draftRef = useRef(draft);
  const isSubmittedRef = useRef(false);

  useEffect(() => {
    draftRef.current = draft;
  });

  useEffect(() => {
    if (!isNewOrder) return;

    const saveDraft = (): void => {
      const { customerId, orderDate, siteMenuOrders } = draftRef.current;
      if (!customerId) return;
      if (!isSubmittedRef.current && siteMenuOrders.length > 0) {
        setCart(toCartCustomerOrder(customerId, orderDate ?? new LocalDate(), siteMenuOrders));
      } else {
        clearCart();
      }
    };

    window.addEventListener('beforeunload', saveDraft);
    return () => {
      window.removeEventListener('beforeunload', saveDraft);
      saveDraft();
    };
  }, []);

  return useCallback(() => {
    isSubmittedRef.current = true;
  }, []);
};

export default useKeepNewOrderInCart;
